import { UTCTimestamp } from 'lightweight-charts'
import { Candle } from '../proto/generated/candle-data-depot-common'

/** Indian market timestamps are stored in UTC ms; shift to IST before extracting the calendar day. */
const IST_OFFSET_SECONDS = 19800

/**
 * All year lines are re-based onto a single reference calendar year so they overlap on one Jan→Dec
 * axis. A leap year is used so a Feb 29 in any source year still maps cleanly.
 */
const REFERENCE_YEAR = 2000

/** Number of most-recent years shown in the compact sidebar widget (current + previous two). */
export const SEASONALS_SIDEBAR_YEAR_COUNT = 3

/** Default number of most-recent years selected when the full page first opens. */
export const SEASONALS_DEFAULT_YEAR_COUNT = 5

/** Colour used for the dashed average curve. */
export const SEASONAL_AVERAGE_COLOR = '#B0B3BA'

/**
 * Distinct line colours, ordered most-recent-first. Chosen to stay legible on the dark chart
 * background and to echo the TradingView seasonals palette (blue = current year).
 * Sized for ~30 years of history before any reuse.
 */
export const SEASONAL_YEAR_COLORS = [
    '#2962FF',
    '#4CAF50',
    '#F5A623',
    '#26C6DA',
    '#EC407A',
    '#AB47BC',
    '#FF7043',
    '#9CCC65',
    '#5C6BC0',
    '#FFCA28',
    '#8D6E63',
    '#E91E63',
    '#00BCD4',
    '#8BC34A',
    '#FF5722',
    '#673AB7',
    '#009688',
    '#CDDC39',
    '#3F51B5',
    '#FFC107',
    '#F06292',
    '#81C784',
    '#64B5F6',
    '#BA68C8',
    '#FFD54F',
    '#4DD0E1',
    '#EF5350',
    '#7E57C2',
    '#29B6F6',
    '#66BB6A',
    '#FFA726',
    '#90A4AE'
]

export type SeasonalsMode = 'percent' | 'absolute'

export type SeasonalPoint = {
    time: UTCTimestamp
    value: number
}

export type SeasonalYearSeries = {
    year: number
    color: string
    /** True for the latest (partial) year — used to draw the trailing marker. */
    isCurrent: boolean
    data: SeasonalPoint[]
}

export type SeasonalDirection = 'up' | 'down' | 'flat'

/** Aggregated month-level cell used by the table view. */
export type SeasonalMonthCell = {
    /** 0-11 */
    month: number
    /**
     * Percent mode: the month's return (%). Value mode: the month-end closing price.
     * Undefined when no data for that month.
     */
    value: number | undefined
    /** Whether price rose/fell/was flat over the month — drives red/green tint and rise/fall tally. */
    direction: SeasonalDirection | undefined
}

export type SeasonalTableRow = {
    year: number
    months: SeasonalMonthCell[]
    /** Percent mode: full-year return (%). Value mode: year-end closing price. */
    total: number | undefined
    /** Whether the year finished above/below/flat vs where it started. */
    totalDirection: SeasonalDirection | undefined
}

type YearCandle = {
    timestampMs: number
    close: number
}

const toIstDate = (timestampMs: number): Date => new Date(timestampMs + IST_OFFSET_SECONDS * 1000)

/** Maps a real candle onto the shared reference year, keeping only month + day. */
const toReferenceTime = (date: Date): UTCTimestamp =>
    (Date.UTC(REFERENCE_YEAR, date.getUTCMonth(), date.getUTCDate()) / 1000) as UTCTimestamp

const colorForIndex = (index: number): string =>
    SEASONAL_YEAR_COLORS[index] ?? SEASONAL_YEAR_COLORS[SEASONAL_YEAR_COLORS.length - 1]

/**
 * Buckets raw candles into per-year, ascending, close-only series keyed by calendar year.
 * Close prices are de-scaled by `priceMultiplier` (candle prices are stored in paise-like units,
 * mirroring how `SecurityRange` normalises them) so value mode shows true prices.
 */
const bucketCandlesByYear = (candles: Candle[], priceMultiplier = 1): Map<number, YearCandle[]> => {
    const divisor = priceMultiplier || 1
    const byYear = new Map<number, YearCandle[]>()
    candles.forEach((candle) => {
        if (!candle.close) return
        const year = toIstDate(candle.startTimestamp).getUTCFullYear()
        const entry: YearCandle = { timestampMs: candle.startTimestamp, close: candle.close / divisor }
        const bucket = byYear.get(year)
        if (bucket) {
            bucket.push(entry)
        } else {
            byYear.set(year, [entry])
        }
    })
    byYear.forEach((bucket) => bucket.sort((a, b) => a.timestampMs - b.timestampMs))
    return byYear
}

const yearsDesc = (byYear: Map<number, YearCandle[]>): number[] =>
    Array.from(byYear.keys()).sort((a, b) => b - a)

const toDirection = (from: number, to: number): SeasonalDirection =>
    to > from ? 'up' : to < from ? 'down' : 'flat'

/** Years present in the data, sorted ascending (oldest → newest). */
export const getAvailableSeasonalYears = (candles: Candle[]): number[] =>
    yearsDesc(bucketCandlesByYear(candles)).reverse()

const resolveTargetYears = (
    availableYearsDesc: number[],
    years: number[] | undefined,
    defaultCount: number
): number[] => {
    const target = years?.length ? years : availableYearsDesc.slice(0, defaultCount)
    return [...target].sort((a, b) => b - a)
}

/**
 * Builds per-year seasonal line series for the requested years, plotted against a shared Jan→Dec
 * calendar axis. In `percent` mode each line is rebased to the % change from the first trading day of
 * that year; in `absolute` mode raw close prices are used.
 */
export const buildSeasonalsData = (
    candles: Candle[],
    options?: { years?: number[]; mode?: SeasonalsMode; yearCount?: number; priceMultiplier?: number }
): SeasonalYearSeries[] => {
    if (!candles.length) return []

    const mode = options?.mode ?? 'percent'
    const byYear = bucketCandlesByYear(candles, options?.priceMultiplier)
    const availableYears = yearsDesc(byYear)
    if (!availableYears.length) return []

    const latestYear = availableYears[0]
    const orderedYears = resolveTargetYears(availableYears, options?.years, options?.yearCount ?? SEASONALS_SIDEBAR_YEAR_COUNT)

    const result: SeasonalYearSeries[] = []
    orderedYears.forEach((year, index) => {
        const bucket = byYear.get(year)
        if (!bucket || bucket.length === 0) return

        const base = bucket[0].close
        if (mode === 'percent' && !base) return

        const data: SeasonalPoint[] = []
        let lastTime = -Infinity
        bucket.forEach((entry) => {
            const time = toReferenceTime(toIstDate(entry.timestampMs))
            // lightweight-charts requires strictly ascending, unique times.
            if (time <= lastTime) return
            lastTime = time
            const value = mode === 'percent' ? (entry.close / base - 1) * 100 : entry.close
            data.push({ time, value })
        })

        if (data.length === 0) return

        result.push({
            year,
            color: colorForIndex(index),
            isCurrent: year === latestYear,
            data
        })
    })

    return result
}

/**
 * Averages the given per-year series onto the shared calendar axis. Each year is forward-filled onto
 * the union of all trading days (carrying its last known value), so every day averages all years —
 * otherwise, since holidays differ per year, a day with only one year's point would make the average
 * jump to touch that single line.
 */
export const buildSeasonalAverageSeries = (series: SeasonalYearSeries[]): SeasonalPoint[] => {
    if (series.length === 0) return []

    const allTimes = Array.from(
        new Set(series.flatMap((s) => s.data.map((point) => point.time as number)))
    ).sort((a, b) => a - b)

    return allTimes.map((time) => {
        let sum = 0
        let count = 0
        series.forEach((s) => {
            // Latest value at or before this day (forward-fill); skip years that haven't started yet.
            let filled: number | undefined
            for (const point of s.data) {
                if ((point.time as number) > time) break
                filled = point.value
            }
            if (filled !== undefined) {
                sum += filled
                count += 1
            }
        })
        return { time: time as UTCTimestamp, value: count > 0 ? sum / count : 0 }
    })
}

/**
 * Builds the table model: one row per year, one cell per calendar month showing the month's change
 * (% or absolute), plus a full-year total, each tagged with a rise/fall/flat direction.
 */
export const buildSeasonalsTable = (
    candles: Candle[],
    options?: { years?: number[]; mode?: SeasonalsMode; yearCount?: number; priceMultiplier?: number }
): SeasonalTableRow[] => {
    if (!candles.length) return []

    const mode = options?.mode ?? 'percent'
    const byYear = bucketCandlesByYear(candles, options?.priceMultiplier)
    const availableYears = yearsDesc(byYear)
    if (!availableYears.length) return []

    const orderedYears = resolveTargetYears(availableYears, options?.years, options?.yearCount ?? SEASONALS_DEFAULT_YEAR_COUNT)

    const rows: SeasonalTableRow[] = []
    orderedYears.forEach((year) => {
        const bucket = byYear.get(year)
        if (!bucket || bucket.length === 0) return

        // First and last close per month.
        const monthFirst: Array<number | undefined> = new Array(12).fill(undefined)
        const monthLast: Array<number | undefined> = new Array(12).fill(undefined)
        bucket.forEach((entry) => {
            const month = toIstDate(entry.timestampMs).getUTCMonth()
            if (monthFirst[month] === undefined) monthFirst[month] = entry.close
            monthLast[month] = entry.close
        })

        const months: SeasonalMonthCell[] = []
        for (let month = 0; month < 12; month += 1) {
            const first = monthFirst[month]
            const last = monthLast[month]
            let value: number | undefined
            let direction: SeasonalDirection | undefined
            if (first !== undefined && last !== undefined && first !== 0) {
                value = mode === 'percent' ? (last / first - 1) * 100 : last
                direction = toDirection(first, last)
            }
            months.push({ month, value, direction })
        }

        const yearBase = bucket[0].close
        const yearLast = bucket[bucket.length - 1].close
        const total =
            yearBase && yearBase !== 0
                ? mode === 'percent'
                    ? (yearLast / yearBase - 1) * 100
                    : yearLast
                : undefined

        rows.push({
            year,
            months,
            total,
            totalDirection: yearBase && yearBase !== 0 ? toDirection(yearBase, yearLast) : undefined
        })
    })

    return rows
}

/** Formats a reference-year timestamp to its short month name (e.g. "Jan"). */
export const formatSeasonalMonth = (time: UTCTimestamp): string =>
    new Date((time as number) * 1000).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })

/** Formats a reference-year timestamp to a day+month label for tooltips (e.g. "20 Sep"). */
export const formatSeasonalDayMonth = (time: UTCTimestamp): string =>
    new Date((time as number) * 1000).toLocaleString('en-US', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC'
    })

/** Formats a seasonal value for display, respecting the chart mode. */
export const formatSeasonalValue = (value: number, mode: SeasonalsMode): string =>
    mode === 'percent'
        ? `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
        : value.toLocaleString('en-IN', { maximumFractionDigits: 2 })

export const MONTH_LABELS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
]
